import { useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { CSVGenerationError, downloadDataToCSV, downloadFile } from '@/utils/dataDownloadUtils';
import { useFileFetcher } from '@/services/queryUtils';
import { useDerivedNetworkContext } from '@/context';
import type { CampaignsByCategory, DateRange, ShowFilter } from '@/types';
import { RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { getCampaignTypesDownloadClickedEventByPageType } from '@/utils/mixpanelUtils/pageTypeHelpers';
import { usePageMetadata } from '@/hooks';

/**
 * Custom hook to handle CSV export for campaign types
 * @returns Object containing handleCSVExport function
 */
const useCampaignTypesCsvExport = () => {
  const queryClient = useQueryClient();
  const fetchFile = useFileFetcher();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { page: pageType } = usePageMetadata();
  const { year, month } = useParams();

  if (!pageType) throw new Error('Page type is required');

  if (pageType !== 'earnings' && pageType !== 'payouts') {
    throw new Error(`Invalid page type: ${pageType}`);
  }

  const fetchPayoutsCampaignsDataForDownload = async (): Promise<CampaignsByCategory> => {
    return await queryClient.fetchQuery({
      queryKey: ['payout-campaigns', selectedNetwork?.id, year, month],
    });
  };

  const handleCSVExport = async (
    dateRange: DateRange | undefined,
    selectedShow: ShowFilter
  ) => {
    mixpanelTrack(getCampaignTypesDownloadClickedEventByPageType(pageType), {
      widget: RevenueDashboardWidget.CampaignTypes,
      page: pageType,
      networkName: selectedNetwork.name || '',
      start: dateRange?.from ?? '',
      end: dateRange?.to ?? '',
      year: year ?? '',
      month: month ?? '',
    });

    if (pageType === 'earnings') {
      if (!dateRange) {
        throw new CSVGenerationError('No data to download', 'campaign_types', 'NO_DATA');
      }

      const showIdsParam = selectedShow && selectedShow !== 'all' ? `&showIds=${selectedShow}` : '';
      const path = `/v2/networks/${selectedNetwork?.id}/ongoing/export/total?from=${dateRange.from}&to=${dateRange.to}&files=campaign-types${showIdsParam}`;
      const { blob, fileName } = await fetchFile(path);
      downloadFile(blob, fileName);
    } else {
      // TODO: replace with call to endpoint that returns ZIP file directly
      const dataToDownload = await fetchPayoutsCampaignsDataForDownload();

      if (!dataToDownload) {
        throw new CSVGenerationError('No data to download', 'campaign_types', 'NO_DATA');
      }

      try {
        await downloadDataToCSV({
          networkName: selectedNetwork.name,
          data: {
            campaign_types: dataToDownload,
          },
          pageType,
          year: year ?? '',
          month: month ?? '',
        });
      } catch (error) {
        if (error instanceof CSVGenerationError) {
          throw new CSVGenerationError(error.message, error.type, error.errorCode);
        }
        throw error;
      }
    }
  };

  return { handleCSVExport };
};

export default useCampaignTypesCsvExport;
